import type { Options } from 'ky'
import { apiClient } from './client'

interface GetPreferredRPCURLRequest {
  /** NOTE: is of ecosystem:chainId format */
  chainId: string

  /**
   * If true, the endpoint record will be returned for the frontend.
   * Namada has different RPCs between frontend and gas station
   * One is blocked by CORS (does not work for frontend). Another one does not support indexing (does not work for gas station).
   */
  isFrontend?: boolean
}

interface GetPreferredEndpointRecordResponse {
  url: string

  // NOTE: some endpoints format the API keys directly in the URL. Others
  // use headers. If headers are used, they are returned in the optional
  // required_headers field.
  requiredHeaders?: Record<string, string>
}

/**
 * getPreferredEndpointRecord fetches the preferred RPC URL for a given chain ID from the backend.
 * This endpoint helps determine the optimal RPC endpoint to use for chain interactions.
 *
 * CONTRACT: a given EndpointRecord is used once for maintaining the RPS bandwidth accounting
 * in our backend. If the client needs to interact with the URL in the EndpointRecord again, it must request a new EndpointRecord.
 * Breaking this invariant may result in the exhaustion of the RPS bandwidth and breakage of both the client
 * and frontend requests.
 *
 * @param request The request object containing the chain ID
 * @returns A promise that resolves to the preferred RPC URL for the chain
 */
export const getPreferredEndpointRecord = (
  request: GetPreferredRPCURLRequest,
  options?: Options,
): Promise<GetPreferredEndpointRecordResponse> =>
  apiClient
    .get('queriers/get-preferred-endpoint-record', {
      searchParams: {
        chainId: request.chainId,
        isFrontend: true,
      },
      ...options,
    })
    .json()
